import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { HourlyDataPoint } from '../version.service';

interface DatasetDef {
  field:  keyof HourlyDataPoint;
  label:  string;
  color:  string;   // solid color used for border (line) and background (bar)
}

const DATASETS: DatasetDef[] = [
  { field: 'generationKwh',  label: '☀️ Ertrag',      color: '#f59e0b' },
  { field: 'consumptionKwh', label: '⚡ Verbrauch',    color: '#3b82f6' },
  { field: 'gridImportKwh',  label: '🔌 Netzbezug',   color: '#ef4444' },
  { field: 'gridExportKwh',  label: '↑ Einspeisung',  color: '#22c55e' },
];

@Component({
  selector: 'app-hourly-chart',
  // Two canvases — one line (all datasets), one bar (single dataset).
  // ng2-charts doesn't support dynamic `type` on a single canvas, so we use *ngIf.
  template: `
    <div style="position:relative;height:clamp(240px,50vh,560px);">
      <canvas *ngIf="!field" baseChart [data]="lineData"  [options]="lineOptions"  type="line"></canvas>
      <canvas *ngIf="field"  baseChart [data]="barData"   [options]="barOptions"   type="bar"></canvas>
    </div>
  `,
})
export class HourlyChartComponent implements OnChanges {
  @Input() data:    HourlyDataPoint[] = [];
  @Input() field:   string | null     = null;   // null → all datasets as lines
  @Input() darkMode = false;

  lineData:    ChartData<'line'> = { datasets: [] };
  lineOptions: ChartConfiguration<'line'>['options'] = {};
  barData:     ChartData<'bar'>  = { datasets: [] };
  barOptions:  ChartConfiguration<'bar'>['options']  = {};

  /** Slot duration in hours, derived from the first two timestamps. */
  private slotHours(): number {
    if (this.data.length < 2) return 1;
    const diff = (this.data[1].ts - this.data[0].ts) / 3_600_000;
    return diff > 0 ? diff : 1;
  }

  /** Convert kWh (per slot) to average kW over that slot. */
  private toKw(kwh: number): number {
    const sh = this.slotHours();
    return kwh / sh;
  }

  private fmtKw(kw: number): string {
    if (Math.abs(kw) < 0.1) return (kw * 1000).toFixed(0) + ' W';
    return kw.toFixed(2) + ' kW';
  }

  private fmtTime(tsMs: number): string {
    return new Date(tsMs).toLocaleTimeString('de-DE', {
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin',
    });
  }

  ngOnChanges(_c: SimpleChanges): void {
    const text  = this.darkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.75)';
    const grid  = this.darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)';
    const labels = this.data.map(p => this.fmtTime(p.ts));

    const sharedScales = {
      x: {
        ticks:  { color: text, maxRotation: 0, font: { size: 11 } },
        grid:   { color: grid },
        title:  { display: true, text: 'Uhrzeit (Berlin)', color: text, font: { size: 11 } },
      },
      y: {
        min: 0,
        ticks: {
          color: text,
          callback: (val: string | number) => this.fmtKw(val as number),
          font: { size: 11 },
        },
        grid:  { color: grid },
        title: { display: true, text: 'Leistung (kW)', color: text, font: { size: 11 } },
      },
    };

    const tooltipLabel = (item: any) =>
      ` ${item.dataset.label}: ${this.fmtKw(item.parsed.y ?? 0)}`;

    // ── Line chart (all datasets) ──────────────────────────────────────────
    this.lineData = {
      labels,
      datasets: DATASETS.map(d => ({
        label:           d.label,
        data:            this.data.map(p => this.toKw(p[d.field] as number)),
        borderColor:     d.color,
        backgroundColor: d.color,
        borderWidth:     2,
        pointRadius:     this.data.length <= 12 ? 3 : 0,
        pointHoverRadius: 5,
        tension:         0.3,
        fill:            false,
      })),
    };
    this.lineOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: { color: text, boxWidth: 12, padding: 12, font: { size: 12 } },
        },
        tooltip: { callbacks: { label: tooltipLabel } },
      },
      scales: sharedScales,
    };

    // ── Bar chart (single dataset) ─────────────────────────────────────────
    const active = DATASETS.find(d => d.field === this.field);
    this.barData = {
      labels,
      datasets: active ? [{
        label:           active.label,
        data:            this.data.map(p => this.toKw(p[active.field] as number)),
        backgroundColor: active.color,
        borderColor:     active.color,
        borderWidth:     0,
      }] : [],
    };
    this.barOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: tooltipLabel } },
      },
      scales: sharedScales,
    };
  }
}
